/**
 * Training Service v2 — Consumes /api/vukz/training/* (P11 consolidated API).
 *
 * All functions return the response shape { success, data, ... } directly,
 * so callers can destructure: const { data } = await getStats('coach');
 */
import { AZURE } from './api';

// ── Group Sessions (F9-C) ──

/**
 * List grouped sessions (staff only). Tenant é resolvido no backend a partir
 * do JWT (g.club_id do DB) — NUNCA enviamos club_id daqui.
 * @param {{ from: string, to: string, planType?: string, status?: string, category?: string, squadId?: number }} opts
 */
export const listGroupSessions = async (opts) => {
  const params = { from: opts.from, to: opts.to };
  if (opts.planType) params.plan_type = opts.planType;
  if (opts.status) params.status = opts.status;
  if (opts.category) params.category_id = opts.category;
  if (opts.squadId) params.squad_id = opts.squadId;
  const { data } = await AZURE.get('/vukz/training/group-sessions', { params });
  return data;
};

/**
 * Group session detail — devolve plan_ids APENAS para abrir o drill-down
 * individual existente (que reautoriza por plano no backend). Nunca usar
 * esses IDs como autorização no cliente.
 * @param {string} sessionKey
 * @param {{ from: string, to: string }} win — mesma janela usada na lista
 */
export const getGroupSessionDetail = async (sessionKey, win) => {
  const { data } = await AZURE.get(`/vukz/training/group-sessions/${sessionKey}`, {
    params: { from: win.from, to: win.to },
  });
  return data;
};

// ── Today Workspace (12/07/2026) ──

/**
 * Central operacional da página Hoje. Lente resolvida SERVER-SIDE pelo role
 * (manager/coach/prep/nutritionist) — nunca enviamos role ou club_id.
 * Filtros apenas restringem o que já está autorizado.
 * @param {{ date?: string, category?: string, squadId?: number, status?: string, planType?: string }} opts
 */
export const getTodayWorkspace = async (opts = {}) => {
  const params = {};
  if (opts.date) params.date = opts.date;
  if (opts.category) params.category_id = opts.category;
  if (opts.squadId) params.squad_id = opts.squadId;
  if (opts.status) params.status = opts.status;
  if (opts.planType) params.plan_type = opts.planType;
  const { data } = await AZURE.get('/vukz/training/today-workspace', { params });
  return data;
};

// ── Dashboard Stats ──

/**
 * Fetch dashboard stats (role-aware).
 * @param {'coach'|'athlete'|'auto'} viewAs
 * @param {number?} clubId
 * @returns {Promise<{role, summary, by_sport}>}
 */
export const getTrainingStats = async (viewAs = 'auto', clubId) => {
  const params = { view_as: viewAs };
  if (clubId) params.club_id = clubId;
  const { data } = await AZURE.get('/vukz/training/dashboard/stats', { params });
  return data;
};

// ── Active Plan ──

/**
 * Fetch today's active plan(s) for an athlete.
 * @param {{ entityId?: number, entityType?: string, date?: string, clubId?: number, sport?: string }} opts
 * @returns {Promise<{date, entity_id, plans, count}>}
 */
export const getActivePlan = async (opts = {}) => {
  const params = {};
  if (opts.entityId) params.entity_id = opts.entityId;
  if (opts.entityType) params.entity_type = opts.entityType;
  if (opts.date) params.date = opts.date;
  if (opts.clubId) params.club_id = opts.clubId;
  if (opts.sport) params.sport = opts.sport;
  const { data } = await AZURE.get('/vukz/training/active-plan', { params });
  return data;
};

// ── Plans CRUD ──

/**
 * List training plans (coach sees created, athlete sees assigned).
 * @param {{ viewAs?: string, clubId?: number, status?: string, dateFrom?: string, dateTo?: string }} opts
 */
export const listPlans = async (opts = {}) => {
  const params = {};
  if (opts.viewAs) params.view_as = opts.viewAs;
  if (opts.clubId) params.club_id = opts.clubId;
  if (opts.status) params.status = opts.status;
  if (opts.dateFrom) params.date_from = opts.dateFrom;
  if (opts.dateTo) params.date_to = opts.dateTo;
  if (opts.planType) params.plan_type = opts.planType;
  // C3: staff vê a timeline de UM atleta do grupo — o backend valida o
  // vínculo em vw_staff_accessible_athletes (403 genérico se fora do escopo).
  if (opts.athleteId) params.athlete_id = opts.athleteId;
  const { data } = await AZURE.get('/vukz/training/plans', { params });
  return data;
};

/**
 * C3: atletas acessíveis ao staff logado (seletor da timeline em modo staff).
 * Fonte: vw_staff_accessible_athletes via JWT — zero parâmetro de entrada.
 */
export const getMyAthletes = async () => {
  const { data } = await AZURE.get('/vukz/training/my-athletes');
  return data;
};

/**
 * Get plan detail with blocks, items, and feedback.
 * @param {number} planId
 */
export const getPlanDetail = async (planId) => {
  const { data } = await AZURE.get(`/vukz/training/plans/${planId}`);
  return data;
};

/**
 * Create a new training plan (coach only).
 * @param {object} payload - { club_id, title, target_type, scheduled_date, blocks, ... }
 *
 * P0-FIX C4c: _skipRetry — o POST de criação é o write IRREVERSÍVEL do
 * lifecycle e NÃO tem idempotência backend (sem client_request_id/UNIQUE).
 * O auto-retry do interceptor em timeout/network/5xx podia re-POSTar um
 * plano já commitado (commit→response é janela real) → plano duplicado.
 * Timeout/network aqui = resultado INDETERMINADO: quem decide é o caller,
 * nunca o interceptor. Ver isIndeterminateCreateError().
 */
export const createPlan = async (payload) => {
  const { data } = await AZURE.post('/vukz/training/plans', payload, { _skipRetry: true });
  return data;
};

/**
 * P0-FIX C4c: distingue falha CONFIRMADA (o backend respondeu — plano NÃO
 * foi criado, rollback total garantido pela TX única) de resultado
 * INDETERMINADO (timeout/network após envio — o commit PODE ter acontecido).
 * No indeterminado: Draft é mantido, zero retry automático, e a UI não deve
 * afirmar categoricamente que o treino não foi criado.
 */
export const isIndeterminateCreateError = (err) =>
  !err?.response && (err?.code === 'ECONNABORTED' || err?.code === 'ERR_NETWORK' || !!err?.request);

/**
 * Create training plan from event (P13 - plan-by-event).
 * @param {object} payload - { club_id, target_event_type, target_event_id, target_event_name, target_event_date, target_type, weeks_before?, ... }
 */
export const createPlanFromEvent = async (payload) => {
  const { data } = await AZURE.post('/vukz/training/plans/from-event', payload);
  return data;
};

/**
 * Update target event on a plan (coach only).
 * @param {number} planId
 * @param {object} target - { target_event_type, target_event_id, target_event_name, ... }
 */
export const updatePlanTarget = async (planId, target) => {
  const { data } = await AZURE.patch(`/vukz/training/plans/${planId}/target`, target);
  return data;
};

/**
 * Athlete completes a plan with feedback.
 * @param {number} planId
 * @param {{ rpe_actual?: number, feeling?: string, notes?: string, completion_pct?: number }} feedback
 */
export const completePlan = async (planId, feedback = {}) => {
  const { data } = await AZURE.post(`/vukz/training/plans/${planId}/complete`, feedback);
  return data;
};

// ── Executions ──

/**
 * Get aggregated execution summary (coach view).
 * @param {{ clubId?: number, days?: number, category?: string }} opts
 */
export const getExecutionsSummary = async (opts = {}) => {
  const params = {};
  if (opts.clubId) params.club_id = opts.clubId;
  if (opts.days) params.days = opts.days;
  if (opts.category) params.category = opts.category;
  const { data } = await AZURE.get('/vukz/training/executions/summary', { params });
  return data;
};

// ── Health ──

export const checkHealth = async () => {
  const { data } = await AZURE.get('/vukz/training/health');
  return data;
};

// ── Publish ──

/**
 * Publish (freeze) a training plan for athlete consumption.
 * Plan must be draft/planned and have blocks with items.
 * @param {number} planId
 */
export const publishPlan = async (planId) => {
  const { data } = await AZURE.post(`/vukz/training/plans/${planId}/publish`);
  return data;
};

// ── Update ──

/**
 * Update a draft/planned training plan.
 * Cannot update published/completed plans.
 * @param {number} planId
 * @param {{ title?: string, description?: string, scheduled_date?: string, scheduled_time?: string, duration_minutes?: number }} payload
 */
export const updatePlan = async (planId, payload) => {
  const { data } = await AZURE.put(`/vukz/training/plans/${planId}`, payload);
  return data;
};

// ── Templates (biblioteca de modelos, 366/367) ──

/**
 * Salva um plano existente como modelo reutilizável (deep copy 4 níveis).
 *
 * NOTA sobre o COALESCE da SP: sp_save_plan_as_template herda título/descrição
 * do plano quando recebe NULL — mas a TELA nunca manda vazio: o modal
 * pré-preenche com plan.title/plan.description e envia sempre o valor visível.
 * O fallback NULL da SP é rede de segurança para OUTROS clientes (agentes),
 * não um caminho que esta UI exercita. Se você leu a SP e veio conferir: é isso.
 *
 * Plano JÁ EXECUTADO pode virar modelo — deliberado (05/08): o modelo copia
 * a PRESCRIÇÃO (blocos/itens/séries), nunca a execução. A SP não checa
 * execuções de propósito.
 *
 * @param {number} planId
 * @param {{ title?: string, description?: string }} payload
 * @returns {Promise<{success: boolean, data: {template_id, blocks_copied, items_copied, sets_copied}}>}
 */
export const savePlanAsTemplate = async (planId, payload = {}) => {
  const { data } = await AZURE.post(`/vukz/training/plans/${planId}/save-as-template`, payload);
  return data;
};

/**
 * Lista os modelos do treinador logado (created_by principal + club_id escopo).
 * Backend lê vw_training_templates_library: cada card já vem com
 * blocks_count/items_count/times_applied/last_applied_date — sem N+1.
 * @param {{ clubId?: number }} opts
 * @returns {Promise<{success, data: {templates: Array, count: number}}>}
 */
export const listTemplates = async (opts = {}) => {
  const params = {};
  if (opts.clubId) params.club_id = opts.clubId;
  const { data } = await AZURE.get('/vukz/training/templates', { params });
  return data;
};

/**
 * Detalhe do modelo com hierarquia completa (template → blocks → items → sets).
 * Não-dono recebe 404 (backend não vaza existência).
 * @param {number} templateId
 */
export const getTemplateDetail = async (templateId) => {
  const { data } = await AZURE.get(`/vukz/training/templates/${templateId}`);
  return data;
};

/**
 * Edição ESTREITA do modelo (paridade ao plano): title/description e/ou
 * sets por item. Ausência não é asserção — sem 'items', sets ficam
 * intactos; items: [{item_id, sets: []}] esvazia o item.
 * Tudo-ou-nada no backend: qualquer set inválido → nada é escrito.
 * Garantia do deep copy: editar o modelo NÃO altera planos já gerados.
 * @param {number} templateId
 * @param {{ title?: string, description?: string, items?: Array<{item_id: number, sets: Array}> }} payload
 */
export const updateTemplate = async (templateId, payload) => {
  const { data } = await AZURE.put(`/vukz/training/templates/${templateId}`, payload);
  return data;
};

/**
 * Aplica um modelo a um squad em N datas (até 50 — MAX_DATES do
 * TrainingDatesPicker é o espelho front desse limite).
 * Atômico no backend: falha em QUALQUER data → rollback total,
 * nenhum treino criado (mensagem de erro do backend já diz isso).
 * @param {number} templateId
 * @param {{ squad_id: number, scheduled_dates: string[], scheduled_time?: string, club_id?: number }} payload
 * @returns {Promise<{success, data: {dates: Array, total_plans_created: number}}>}
 */
export const applyTemplate = async (templateId, payload) => {
  const { data } = await AZURE.post(`/vukz/training/templates/${templateId}/apply`, payload);
  return data;
};

/**
 * Arquiva o modelo (soft delete: is_active=0). Planos já gerados mantêm
 * template_id apontando para o modelo arquivado — histórico preservado.
 * @param {number} templateId
 */
export const archiveTemplate = async (templateId) => {
  const { data } = await AZURE.delete(`/vukz/training/templates/${templateId}`);
  return data;
};

// ── Swimming Data ──

/**
 * Get swimming summary per athlete (coach view).
 * Returns PBs, last competition, medals for each linked athlete.
 * @param {{ clubId?: number }} opts
 */
export const getAthletesSwimSummary = async (opts = {}) => {
  const params = {};
  if (opts.clubId) params.club_id = opts.clubId;
  const { data } = await AZURE.get('/vukz/training/athletes/swim-summary', { params });
  return data;
};

/**
 * Get athlete's own swimming results (athlete view).
 * @param {{ limit?: number, offset?: number, pbsOnly?: boolean }} opts
 */
export const getMySwimResults = async (opts = {}) => {
  const params = {};
  if (opts.limit) params.limit = opts.limit;
  if (opts.offset) params.offset = opts.offset;
  if (opts.pbsOnly) params.pbs_only = 'true';
  const { data } = await AZURE.get('/vukz/training/athlete/swim-results', { params });
  return data;
};

// ── Execution Canvas ──

/**
 * Create execution from plan (idempotent: returns existing if already created).
 * @param {number} planId
 * @param {number?} athleteUserId
 */
export const createExecution = async (planId, athleteUserId) => {
  const payload = { plan_id: planId };
  if (athleteUserId) payload.athlete_user_id = athleteUserId;
  const { data } = await AZURE.post('/training/executions', payload);
  return data;
};

/**
 * Get full execution with blocks + items.
 * @param {number} executionId
 */
export const getExecution = async (executionId) => {
  const { data } = await AZURE.get(`/training/executions/${executionId}`);
  return data;
};

/**
 * Get execution by plan + athlete.
 * @param {number} planId
 * @param {number?} athleteId
 */
export const getExecutionByPlan = async (planId, athleteId) => {
  const params = {};
  if (athleteId) params.athlete_id = athleteId;
  const { data } = await AZURE.get(`/training/executions/by-plan/${planId}`, { params });
  return data;
};

/**
 * Update execution header (RPE, fatigue, soreness, notes, etc.).
 * @param {number} executionId
 * @param {Object} fields
 */
export const updateExecution = async (executionId, fields) => {
  const { data } = await AZURE.patch(`/training/executions/${executionId}`, fields);
  return data;
};

/**
 * Update a single execution item (is_done, notes, metrics).
 * @param {number} executionId
 * @param {number} itemId
 * @param {Object} fields
 */
export const updateExecutionItem = async (executionId, itemId, fields) => {
  const { data } = await AZURE.patch(`/training/executions/${executionId}/items/${itemId}`, fields);
  return data;
};

/**
 * Complete execution with final metrics.
 * @param {number} executionId
 * @param {Object} finalData
 */
export const completeExecution = async (executionId, finalData = {}) => {
  const { data } = await AZURE.post(`/training/executions/${executionId}/complete`, finalData);
  return data;
};

// ── Today's Sessions ──

/**
 * Get today's planned sessions (coach view).
 * Calls GET /api/training/sessions/today
 * @param {{ clubId?: number, category?: string }} opts
 * @returns {Promise<{ success, data: Array<{plan_id, title, scheduled_date, scheduled_time, status, target_name, total_volume_m, focus_category, group_name}> }>}
 */
export const getTodaySessions = async (opts = {}) => {
  const params = {};
  if (opts.clubId) params.club_id = opts.clubId;
  if (opts.category) params.category = opts.category;
  const { data } = await AZURE.get('/training/sessions/today', { params });
  return data;
};
